import * as React from "react";
import { type VariantProps, cva } from "class-variance-authority";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

const alertVariants = cva(
  "flex items-start gap-3 px-4 py-3 rounded-ev border text-sm",
  {
    variants: {
      variant: {
        success:
          "bg-ev-success-bg text-ev-success-text border-ev-success-border",
        warning:
          "bg-ev-warning-bg text-ev-warning-text border-ev-warning-border",
        destructive:
          "bg-ev-danger-bg text-ev-danger-text border-ev-danger-border",
        info: "bg-ev-info-bg text-ev-info-text border-ev-info-border",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

const variantIcons: Record<string, React.ReactNode> = {
  success: (
    <CheckCircle2
      className="w-4 h-4 shrink-0 mt-0.5"
      aria-hidden="true"
    />
  ),
  warning: (
    <AlertTriangle
      className="w-4 h-4 shrink-0 mt-0.5"
      aria-hidden="true"
    />
  ),
  destructive: (
    <XCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
  ),
  info: (
    <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
  ),
};

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  message?: string;
  description?: string;
  title?: string;
  onDismiss?: () => void;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    { className, variant = "info", message, description, title, onDismiss, ...props },
    ref,
  ) => {
    const body = message ?? description;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {variantIcons[variant ?? "info"]}
        <div className="flex-1">
          {title && <p className="font-semibold">{title}</p>}
          {body && <span>{body}</span>}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-auto hover:opacity-70"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  },
);
Alert.displayName = "Alert";

export { Alert, alertVariants };
